import React, { useEffect, useMemo, useState } from "react";
import { Link } from 'react-router-dom';
import { baseUrl } from '../../config.js';
import './Dashboard.css';
import useToken from '../../useToken';

const IconBox = () => (
    <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M21 8H3l2-4h14l2 4Z" />
        <path d="M3 8v11a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1V8" />
        <path d="M12 8v12" />
    </svg>
);

const IconCheck = () => (
    <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M20 6 9 17l-5-5" />
    </svg>
);

const IconStar = () => (
    <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 3.2l2.6 5.3 5.8.8-4.2 4.1 1 5.8L12 16.4 6.8 19.2l1-5.8L3.6 9.3l5.8-.8L12 3.2z" />
    </svg>
);

const statusLabels = {
    Booked: 'Confirmada',
    Approved: 'Aprobada',
    Waiting: 'En espera',
    Partial: 'Parcial',
    Canceled: 'Cancelada',
};

const formatRelative = (dateStr) => {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Hace un momento';
    if (mins < 60) return `Hace ${mins} min`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `Hace ${hrs} h`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `Hace ${days} día${days > 1 ? 's' : ''}`;
    return new Date(dateStr).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' });
};

const classifyBooking = (booking, now = new Date()) => {
    const start = new Date(booking.startDate);
    const end = new Date(booking.endDate);
    if (booking.status === 'Booked' && start <= now && end >= now) return 'ongoing';
    if (['Booked', 'Approved', 'Waiting'].includes(booking.status) && start > now) return 'upcoming';
    if (booking.status === 'Booked' && end < now) return 'ended';
    return 'other';
};

const Dashboard = () => {
    const { token } = useToken();
    const [loading, setLoading] = useState(true);
    const [info, setInfo] = useState({ bookings: 0, favs: 0, notifications: { client: 0 } });
    const [bookings, setBookings] = useState([]);

    useEffect(() => {
        if (token) {
            obtenerDatos();
        } else {
            setLoading(false);
        }
    }, [token]);

    const obtenerDatos = async () => {
        setLoading(true);
        try {
            const headers = { Authorization: `Bearer ${token}` };
            const [infoRes, bookingsRes] = await Promise.all([
                fetch(baseUrl + 'User/Info', { headers }),
                fetch(baseUrl + 'Bookings/', { headers }),
            ]);
            const infoJson = await infoRes.json();
            const bookingsJson = await bookingsRes.json();

            if (infoJson.success) {
                setInfo(infoJson.data);
            }
            if (bookingsJson.success) {
                setBookings(bookingsJson.data || []);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const stats = useMemo(() => {
        const now = new Date();
        let ongoing = 0;
        let upcoming = 0;
        let ended = 0;
        let totalSpaces = 0;

        bookings.forEach((b) => {
            const kind = classifyBooking(b, now);
            if (kind === 'ongoing') {
                ongoing += 1;
                totalSpaces += Number(b.spaces) || 0;
            } else if (kind === 'upcoming') {
                upcoming += 1;
            } else if (kind === 'ended') {
                ended += 1;
            }
        });

        return { ongoing, upcoming, ended, totalSpaces };
    }, [bookings]);

    const statusBreakdown = useMemo(() => {
        const total = stats.ongoing + stats.upcoming + stats.ended;
        return [
            { key: 'ongoing', label: 'En curso', count: stats.ongoing, pct: total ? (stats.ongoing / total) * 100 : 0 },
            { key: 'upcoming', label: 'Próximas', count: stats.upcoming, pct: total ? (stats.upcoming / total) * 100 : 0 },
            { key: 'ended', label: 'Finalizadas', count: stats.ended, pct: total ? (stats.ended / total) * 100 : 0 },
        ];
    }, [stats]);

    const recentBookings = useMemo(() => {
        return [...bookings]
            .sort((a, b) => new Date(b.dateRegister || b.startDate) - new Date(a.dateRegister || a.startDate))
            .slice(0, 5);
    }, [bookings]);

    const storeSummary = useMemo(() => {
        const map = {};
        bookings.forEach((b) => {
            const name = b.name || b.street || `Reservación #${b.idBooking}`;
            if (!map[name]) {
                map[name] = { name, count: 0, spaces: 0 };
            }
            map[name].count += 1;
            if (classifyBooking(b) === 'ongoing') {
                map[name].spaces += Number(b.spaces) || 0;
            }
        });
        return Object.values(map)
            .sort((a, b) => b.count - a.count)
            .slice(0, 6);
    }, [bookings]);

    const activePercent = stats.ongoing + stats.upcoming + stats.ended
        ? (stats.ongoing / (stats.ongoing + stats.upcoming + stats.ended)) * 100
        : 0;
    const circumference = 565.48;
    const strokeDasharray = `${(activePercent / 100) * circumference} ${circumference}`;

    const metrics = [
        { key: 'total', label: 'Total reservaciones', value: info.bookings ?? bookings.length, accent: true },
        { key: 'ongoing', label: 'Activas ahora', value: stats.ongoing },
        { key: 'upcoming', label: 'Próximas', value: stats.upcoming },
        { key: 'favs', label: 'Favoritos', value: info.favs ?? 0 },
    ];

    return (
        <div className="dash">
            <header className="dash-header">
                <div>
                    <h1>Dashboard</h1>
                    <p>Resumen de tus reservaciones y actividad reciente</p>
                </div>
            </header>

            <div className="dash-metrics">
                {metrics.map((m) => (
                    <article key={m.key} className={'dash-metric' + (m.accent ? ' is-accent' : '')}>
                        <span className="dash-metric-value">{loading ? '—' : m.value}</span>
                        <span className="dash-metric-label">{m.label}</span>
                    </article>
                ))}
            </div>

            <div className="dash-grid">
                <section className="dash-card">
                    <div className="dash-card-head">
                        <h2>Estado de reservaciones</h2>
                    </div>
                    <div className="dash-chart">
                        <svg viewBox="0 0 200 200" className="dash-donut" aria-hidden="true">
                            <circle cx="100" cy="100" r="90" fill="none" stroke="#e8edf5" strokeWidth="20" />
                            <circle
                                cx="100"
                                cy="100"
                                r="90"
                                fill="none"
                                stroke="#f27830"
                                strokeWidth="20"
                                strokeDasharray={strokeDasharray}
                                strokeLinecap="round"
                                transform="rotate(-90 100 100)"
                            />
                            <text x="100" y="95" textAnchor="middle" className="dash-donut-num">
                                {loading ? '—' : `${activePercent.toFixed(0)}%`}
                            </text>
                            <text x="100" y="115" textAnchor="middle" className="dash-donut-lbl">
                                en curso
                            </text>
                        </svg>
                        <div className="dash-legend">
                            {statusBreakdown.map((item) => (
                                <div key={item.key}>
                                    <i className={item.key === 'ongoing' ? 'ocupado' : 'libre'} />
                                    {item.label}
                                    <b>{loading ? '—' : item.count}</b>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="dash-card">
                    <div className="dash-card-head">
                        <h2>Reservaciones por almacén</h2>
                    </div>
                    <div className="dash-progress">
                        {loading && <p className="dash-empty">Cargando…</p>}
                        {!loading && storeSummary.length === 0 && (
                            <p className="dash-empty">Aún no tienes reservaciones.</p>
                        )}
                        {!loading && storeSummary.map((store) => {
                            const max = Math.max(...storeSummary.map((s) => s.count), 1);
                            const pct = (store.count / max) * 100;
                            return (
                                <div key={store.name}>
                                    <div className="dash-progress-meta">
                                        <span>{store.name}</span>
                                        <b>{store.count} reservación{store.count !== 1 ? 'es' : ''}</b>
                                    </div>
                                    <div className="dash-progress-bar">
                                        <span style={{ width: `${pct}%` }} />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </section>
            </div>

            <div className="dash-grid">
                <section className="dash-card">
                    <div className="dash-card-head">
                        <h2>Tus reservaciones</h2>
                        <Link to="/reservaciones" className="dash-link">Ver todas →</Link>
                    </div>
                    <div className="dash-table-wrap">
                        <table className="dash-table">
                            <thead>
                                <tr>
                                    <th>Almacén</th>
                                    <th>Espacios</th>
                                    <th>Estado</th>
                                    <th>Inicio</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading && (
                                    <tr>
                                        <td colSpan="4" className="dash-empty">Cargando…</td>
                                    </tr>
                                )}
                                {!loading && recentBookings.length === 0 && (
                                    <tr>
                                        <td colSpan="4" className="dash-empty">No hay reservaciones todavía.</td>
                                    </tr>
                                )}
                                {!loading && recentBookings.map((b) => {
                                    const kind = classifyBooking(b);
                                    const statusClass = kind === 'ongoing' ? 'dash-status--warn' : 'dash-status--ok';
                                    const statusText = kind === 'ongoing'
                                        ? 'En curso'
                                        : kind === 'upcoming'
                                            ? 'Próxima'
                                            : kind === 'ended'
                                                ? 'Finalizada'
                                                : (statusLabels[b.status] || b.status);
                                    return (
                                        <tr key={b.idBooking}>
                                            <td className="name">{b.name || b.street || `#${b.idBooking}`}</td>
                                            <td>{b.spaces ?? '—'}</td>
                                            <td><span className={`dash-status ${statusClass}`}>{statusText}</span></td>
                                            <td>{b.startDate ? new Date(b.startDate).toLocaleDateString('es-MX') : '—'}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </section>

                <section className="dash-card">
                    <div className="dash-card-head">
                        <h2>Actividad reciente</h2>
                    </div>
                    <ul className="dash-activity">
                        {loading && <li><span className="dash-empty">Cargando…</span></li>}
                        {!loading && recentBookings.length === 0 && (
                            <li><span className="dash-empty">Sin actividad reciente.</span></li>
                        )}
                        {!loading && recentBookings.map((b, i) => (
                            <li key={b.idBooking}>
                                <span className="dash-activity-icon">
                                    {i === 0 ? <IconBox /> : classifyBooking(b) === 'ended' ? <IconCheck /> : <IconStar />}
                                </span>
                                <div>
                                    <strong>
                                        {classifyBooking(b) === 'ongoing' ? 'Reservación en curso' : 'Reservación registrada'}
                                        {' — '}{b.name || b.street || `#${b.idBooking}`}
                                    </strong>
                                    <em>{formatRelative(b.dateRegister || b.startDate)}</em>
                                </div>
                            </li>
                        ))}
                    </ul>
                    {!loading && (info.notifications?.client > 0) && (
                        <p className="dash-notice">
                            Tienes {info.notifications.client} notificación{info.notifications.client !== 1 ? 'es' : ''} sin leer.
                        </p>
                    )}
                </section>
            </div>
        </div>
    );
};

export default Dashboard;
